import type { useChat } from "@ai-sdk/react";
import type { ChatPlan } from "@repo/shared/types/chat-plan";
import {
  MENTION_CONTEXT_SENTINEL,
  type MentionKind,
  type MentionPartData,
  MentionPartDataSchema,
} from "@repo/shared/types/mention-part";
import { CodeBlock } from "@repo/ui/ai-elements/code-block";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@repo/ui/ai-elements/conversation";
import {
  flattenToString,
  markdownComponents,
} from "@repo/ui/ai-elements/markdown-components";
import {
  defaultUrlTransform,
  Message,
  MessageAttachment,
  MessageContent,
  MessageLoading,
  MessageResponse,
  type StreamdownComponents,
  type UrlTransform,
} from "@repo/ui/ai-elements/message";
import { Reasoning } from "@repo/ui/ai-elements/reasoning";
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from "@repo/ui/ai-elements/tool";
import { Button } from "@repo/ui/button";
import { Icon } from "@repo/ui/components/icon";
import { SqlCodeBlock } from "@repo/ui/components/sql-code-block";
import { cn } from "@repo/ui/lib/utils";
import { Skeleton } from "@repo/ui/ui/skeleton";
import { Fragment, isValidElement, type ReactNode } from "react";

import { AskUserForm } from "./ask-user-form";
import { MentionChip } from "./chat-input-editor";
import { CONTINUATION_SENTINEL, TOOL_LABELS } from "./constants";
import { PlanCard } from "./plan-card";
import { type CreateChartToolOutput, RunResultChart } from "./run-result-chart";
import { type RunQueryToolOutput, RunResultTable } from "./run-result-table";
import { SqlBlockActionsMenu } from "./sql-block-actions";
import type {
  AskUserAnswer,
  AskUserResumeData,
  AskUserSuspendPayload,
  ChatErrorPartData,
  ChatNoticePartData,
  ToolCallApprovalData,
  ToolResumeData,
} from "./types";

/**
 * Renders a single inline run of text + mention parts as one wrapping
 * paragraph. Used for user-message bubbles so the chips sit *inside* the
 * sentence instead of stacking below it. Whitespace is preserved so the
 * spaces around each pill from the editor's text runs survive.
 */
const InlineUserRun = ({
  parts,
}: {
  parts: Array<
    | { kind: "text"; key: string; text: string }
    | {
        kind: "mention";
        key: string;
        data: MentionPartData;
      }
  >;
}) => (
  <div className="wrap-break-word whitespace-pre-wrap text-xs/relaxed">
    {parts.map((p) =>
      p.kind === "text" ? (
        <Fragment key={p.key}>{p.text}</Fragment>
      ) : (
        <MentionChip
          key={p.key}
          id={p.data.entityId}
          label={p.data.label}
          kind={p.data.kind}
        />
      ),
    )}
  </div>
);

/**
 * Validates an incoming `data-mention` part payload. Past turns persisted by
 * older clients (or future kinds we don't recognise) gracefully no-op.
 */
const parseMentionPartData = (data: unknown): MentionPartData | null => {
  const parsed = MentionPartDataSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
};

/**
 * Private URL scheme used by the supervisor to inline rich entity chips in
 * its replies. The agent writes `[<label>](mention:<kind>/<id>)` in plain
 * markdown; the `components.a` override below rewrites those anchors to the
 * shared {@link MentionChip} so AI-emitted references render identically to
 * user-typed `@`-mentions. See `docs/agents/chat-mentions.md`.
 */
const AI_MENTION_URL =
  /^mention:(?:\/\/)?(block|board|dataset|datasource)\/(.+)$/;

/**
 * Recursively flatten a markdown link's children into a single string for
 * the chip's `label`. Most cases are a plain string; nested formatting
 * (`[**bold**](mention:…)`) collapses cleanly.
 */
const flattenChildrenText = (node: ReactNode): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenChildrenText).join("");
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode } | null | undefined;
    return flattenChildrenText(props?.children);
  }
  return "";
};

/**
 * Allow the private `mention:` scheme through Streamdown's default URL
 * sanitizer, which would otherwise strip unknown protocols and drop `href`.
 * Anything else falls through to the default transform unchanged.
 */
const assistantUrlTransform: UrlTransform = (url, key, node) => {
  if (typeof url === "string" && url.startsWith("mention:")) return url;
  return defaultUrlTransform(url, key, node);
};

/**
 * Streamdown `components` override applied to assistant text. When the agent
 * writes `[<label>](mention:<kind>/<id>)` we render the shared
 * {@link MentionChip}; otherwise the anchor renders as-is.
 */
const assistantMessageComponents: StreamdownComponents = {
  a: ({ href, children, node: _node, ...props }) => {
    const match = href ? AI_MENTION_URL.exec(href) : null;
    if (!match) {
      // Mark as a link so MessageResponse's shared `[data-streamdown=link]`
      // styling (blue, underlined) applies — the default attribute is dropped
      // when the `a` component is overridden.
      return (
        <a href={href} data-streamdown="link" {...props}>
          {children}
        </a>
      );
    }
    const [, kind, rawId] = match;
    const id = decodeURIComponent(rawId);
    const label = flattenChildrenText(children) || id;
    return <MentionChip kind={kind as MentionKind} id={id} label={label} />;
  },
  // Layer a "Save as dataset" action onto SQL fences the agent tags with a
  // datasource (```sql datasource=<id>). Streamdown surfaces the fence meta as
  // a `metastring` prop (allow-listed in `MessageResponse`'s sanitize schema).
  // Non-SQL fences delegate to the shared markdown code renderer unchanged.
  code: (props) => {
    const className = (props as { className?: string }).className;
    const raw = /language-(\w+)/.exec(className ?? "")?.[1];
    if (raw !== "sql") {
      const Base = markdownComponents.code;
      return typeof Base === "function" ? (
        <Base {...props} />
      ) : (
        <code {...props} />
      );
    }
    const children = (props as { children?: ReactNode }).children;
    const code = flattenToString(children).replace(/\n$/, "");
    const meta = (props as { metastring?: string }).metastring ?? "";
    const datasourceId = /(?:^|\s)datasource=([\w-]+)/.exec(meta)?.[1];
    return (
      <SqlCodeBlock
        code={code}
        className="my-3 border-border"
        actions={
          datasourceId ? (
            <SqlBlockActionsMenu sql={code} datasourceId={datasourceId} />
          ) : undefined
        }
      />
    );
  },
};

type UseChatReturn = ReturnType<typeof useChat>;
type ChatMessage = UseChatReturn["messages"][number];

export type ChatMessagesProps = {
  messages: UseChatReturn["messages"];
  isBusy: boolean;
  onToolApproval: (approval: ToolCallApprovalData, approved: boolean) => void;
  onToolResume: (
    approval: ToolCallApprovalData,
    resumeData: ToolResumeData,
  ) => void;
  /**
   * Re-runs the last turn. When provided, a Retry button is shown beneath a
   * `data-error` on the final message (while not streaming).
   */
  onRetry?: () => void;
  /**
   * Approves the latest presented plan: flips the chat back to Auto mode and
   * sends an execution message. Only the most recent plan card in the
   * transcript shows the Approve button.
   */
  onApprovePlan?: () => void;
  /**
   * Extra classes for the inner content wrapper (`ConversationContent`). Used
   * by the full-page layout to constrain the messages to a centered reading
   * column while the scroll container itself spans the full pane width.
   */
  contentClassName?: string;
};

/**
 * Suspend / approval metadata persisted by Mastra on the assistant message
 * itself (`message.content.metadata.{suspendedTools,pendingToolApprovals}`).
 * Mirrors the upstream shape (`@mastra/core` agentic-loop tool step) and is
 * preserved through `AIV5Adapter`'s metadata merge on hydration.
 */
type PersistedToolMeta = {
  toolCallId: string;
  toolName: string;
  args?: unknown;
  runId: string;
  suspendPayload?: unknown;
};

type MessageMetadataWithSuspends = {
  suspendedTools?: Record<string, PersistedToolMeta>;
  pendingToolApprovals?: Record<string, PersistedToolMeta>;
};

/**
 * Builds the toolCallId → approval map for one persisted message. Live
 * `data-tool-call-*` parts (streamed during the active turn) win because
 * they carry the freshest payload; on reload we fall back to
 * `metadata.suspendedTools` / `metadata.pendingToolApprovals` so the form /
 * Approve-Deny UI reappears with the persisted `runId`. Without this
 * fallback, hydrated messages would render the matching `tool-*` part as
 * "expired" even though the Mastra workflow snapshot is still resumable.
 */
const buildApprovalsMap = (message: ChatMessage) => {
  const approvals = new Map<string, ToolCallApprovalData>();

  for (const p of message.parts) {
    if (
      p.type === "data-tool-call-approval" ||
      p.type === "data-tool-call-suspended"
    ) {
      const data = p.data as ToolCallApprovalData;
      approvals.set(data.toolCallId, data);
    }
  }

  const meta = (message as { metadata?: MessageMetadataWithSuspends }).metadata;
  for (const m of Object.values(meta?.suspendedTools ?? {})) {
    if (!approvals.has(m.toolCallId)) {
      approvals.set(m.toolCallId, {
        runId: m.runId,
        toolCallId: m.toolCallId,
        toolName: m.toolName,
        suspendPayload: m.suspendPayload,
      });
    }
  }
  for (const m of Object.values(meta?.pendingToolApprovals ?? {})) {
    if (!approvals.has(m.toolCallId)) {
      approvals.set(m.toolCallId, {
        runId: m.runId,
        toolCallId: m.toolCallId,
        toolName: m.toolName,
        args: m.args,
      });
    }
  }

  return approvals;
};

/**
 * Returns true when any persisted tool call is currently awaiting user
 * approval (an approval/suspended data part exists for a tool whose state
 * has not yet resolved). Used to suppress the "Thinking..." indicator while
 * the chat is paused waiting for the user.
 */
const hasPendingApprovalIn = (messages: UseChatReturn["messages"]): boolean => {
  for (const msg of messages) {
    const approvals = buildApprovalsMap(msg);
    if (approvals.size === 0) continue;
    for (const p of msg.parts) {
      if (
        "toolCallId" in p &&
        typeof p.type === "string" &&
        p.type.startsWith("tool-") &&
        approvals.has(p.toolCallId) &&
        p.state !== "output-available" &&
        p.state !== "output-error"
      ) {
        return true;
      }
    }
  }
  return false;
};

/**
 * True for the synthetic "keep going" user message the client injects to
 * auto-continue a turn that hit its step budget. These carry the
 * {@link CONTINUATION_SENTINEL} prefix so we can hide the whole bubble — the
 * turn should look continuous to the user even though it spans multiple
 * server segments. The message still ships to the LLM as a normal nudge and
 * is persisted, so this also hides it on reload.
 */
const isContinuationMessage = (message: ChatMessage): boolean =>
  message.role === "user" &&
  message.parts.some(
    (p) =>
      p.type === "text" && p.text.trimStart().startsWith(CONTINUATION_SENTINEL),
  );

const isAskUserSuspendPayload = (
  value: unknown,
): value is AskUserSuspendPayload =>
  !!value &&
  typeof value === "object" &&
  "questions" in (value as Record<string, unknown>) &&
  Array.isArray((value as { questions: unknown }).questions);

/**
 * Finds the `${messageIndex}-${partIndex}` key of the most recent
 * `presentPlan` tool call whose output is available. Only that plan shows the
 * Approve button — earlier plans stay readable but inert. Returns null when no
 * plan has been presented.
 */
const findLatestPlanKey = (
  messages: UseChatReturn["messages"],
): string | null => {
  for (let mi = messages.length - 1; mi >= 0; mi--) {
    const parts = messages[mi]?.parts ?? [];
    for (let pi = parts.length - 1; pi >= 0; pi--) {
      const part = parts[pi];
      if (
        part?.type === "tool-presentPlanTool" &&
        "state" in part &&
        part.state === "output-available"
      ) {
        return `${mi}-${pi}`;
      }
    }
  }
  return null;
};

export const ChatMessages = ({
  messages,
  isBusy,
  onToolApproval,
  onToolResume,
  onRetry,
  onApprovePlan,
  contentClassName,
}: ChatMessagesProps) => {
  const latestPlanKey = findLatestPlanKey(messages);
  return (
    <Conversation className="h-full" initial="instant">
      <ConversationContent className={cn("py-2", contentClassName)}>
        {messages.map((message, index) => {
          const { id, role, parts } = message;
          // Auto-continuation nudges are invisible: the turn should read as one
          // continuous response even though it spans multiple server segments.
          if (isContinuationMessage(message)) return null;
          const isLastMessage = index === messages.length - 1;
          return (
            <Message from={role} key={index}>
              <MessageContent>
                {(() => {
                  const approvals = buildApprovalsMap(message);

                  const rendered: ReactNode[] = [];
                  let inlineRun: Array<
                    | { kind: "text"; key: string; text: string }
                    | { kind: "mention"; key: string; data: MentionPartData }
                  > = [];

                  const flushInlineRun = () => {
                    if (inlineRun.length === 0) return;
                    rendered.push(
                      <InlineUserRun
                        key={`${id}-run-${rendered.length}`}
                        parts={inlineRun}
                      />,
                    );
                    inlineRun = [];
                  };

                  parts.forEach((part, i) => {
                    if (
                      part.type === "data-tool-call-approval" ||
                      part.type === "data-tool-call-suspended"
                    )
                      return;

                    // User bubbles: coalesce contiguous text + mention parts
                    // into one wrapping paragraph so chips sit inside the
                    // sentence (matches the input box). Markdown is meaningless
                    // here — the editor is plain-text only.
                    if (role === "user") {
                      if (part.type === "text") {
                        // The server appends a sentinel-wrapped text part with
                        // resolved mention context for the LLM. Hide it from
                        // the bubble — chips below already convey the same
                        // information visually.
                        if (
                          part.text
                            .trimStart()
                            .startsWith(MENTION_CONTEXT_SENTINEL)
                        ) {
                          return;
                        }
                        inlineRun.push({
                          kind: "text",
                          key: `${id}-${i}`,
                          text: part.text,
                        });
                        return;
                      }
                      if (part.type === "data-mention") {
                        const data = parseMentionPartData(part.data);
                        if (data) {
                          inlineRun.push({
                            kind: "mention",
                            key: `${id}-${i}`,
                            data,
                          });
                        }
                        return;
                      }
                    }

                    flushInlineRun();

                    switch (part.type) {
                      case "text":
                        rendered.push(
                          <MessageResponse
                            key={`${id}-${i}`}
                            components={assistantMessageComponents}
                            urlTransform={assistantUrlTransform}
                          >
                            {part.text}
                          </MessageResponse>,
                        );
                        return;
                      case "reasoning":
                        rendered.push(
                          <Reasoning
                            key={`${id}-${i}`}
                            text={part.text}
                            state={part.state}
                          />,
                        );
                        return;
                      case "data-error": {
                        const data = part.data as ChatErrorPartData | undefined;
                        rendered.push(
                          <div
                            key={`${id}-${i}`}
                            className="flex items-center gap-1.5 text-destructive"
                          >
                            <Icon
                              name="warning"
                              className="size-3.5 shrink-0"
                            />
                            <span>
                              {data?.message || "Something went wrong."}
                            </span>
                          </div>,
                        );
                        if (onRetry && isLastMessage && !isBusy) {
                          rendered.push(
                            <div key={`${id}-${i}-retry`}>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={onRetry}
                              >
                                Retry
                              </Button>
                            </div>,
                          );
                        }
                        return;
                      }
                      case "data-notice": {
                        const data = part.data as
                          | ChatNoticePartData
                          | undefined;
                        rendered.push(
                          <div
                            key={`${id}-${i}`}
                            className="flex items-center gap-1.5 text-muted-foreground text-xs"
                          >
                            <Icon
                              name="info-circle"
                              className="size-3.5 shrink-0"
                            />
                            <span>{data?.message || "The agent stopped."}</span>
                          </div>,
                        );
                        return;
                      }
                      case "data-tripwire": {
                        const data = part.data as
                          | { reason?: string; message?: string }
                          | undefined;
                        rendered.push(
                          <div
                            key={`${id}-${i}`}
                            className="flex items-center gap-1.5 text-destructive"
                          >
                            <Icon
                              name="warning"
                              className="size-3.5 shrink-0"
                            />
                            <span>
                              {data?.message ||
                                data?.reason ||
                                "The response was blocked."}
                            </span>
                          </div>,
                        );
                        return;
                      }
                      case "data-mention": {
                        // Assistant-side fallback (mentions in assistant
                        // messages aren't expected today, but render as a chip
                        // for forward-compat).
                        const data = parseMentionPartData(part.data);
                        if (!data) return;
                        rendered.push(
                          <MentionChip
                            key={`${id}-${i}`}
                            id={data.entityId}
                            label={data.label}
                            kind={data.kind}
                          />,
                        );
                        return;
                      }
                      case "file":
                        if (part.mediaType?.startsWith("image/")) {
                          rendered.push(
                            <MessageAttachment
                              key={`${id}-${i}`}
                              data={part}
                            />,
                          );
                        }
                        return;
                      default: {
                        if (
                          !("toolCallId" in part) ||
                          !part.type.startsWith("tool-")
                        )
                          return;
                        const toolType = part.type as `tool-${string}`;
                        const label = TOOL_LABELS[toolType];

                        // presentPlan: render the structured plan as an
                        // approvable card. The Approve button only shows on the
                        // most recent plan in the transcript (`latestPlanKey`);
                        // earlier plans stay readable but inert.
                        if (
                          toolType === "tool-presentPlanTool" &&
                          part.state === "output-available" &&
                          part.output
                        ) {
                          rendered.push(
                            <PlanCard
                              key={`${id}-${i}`}
                              plan={part.output as ChatPlan}
                              showApprove={
                                latestPlanKey === `${index}-${i}` &&
                                !!onApprovePlan
                              }
                              disabled={isBusy}
                              onApprove={() => onApprovePlan?.()}
                            />,
                          );
                          return;
                        }

                        // While the input is still streaming, `part.input` is a
                        // partial object: the descriptive subtitle field often
                        // arrives before the `action` the dynamic title depends
                        // on, which would make the subtitle render before the
                        // (final) title. Gate both on the input being complete so
                        // they resolve together.
                        const inputReady = part.state !== "input-streaming";
                        const resolvedTitle = inputReady
                          ? (label?.getTitle?.({
                              input: part.input,
                              output: part.output,
                            }) ?? label?.title)
                          : label?.title;

                        // runQuery: render the call as a compact collapsible
                        // (SQL only) with the actual result as a real data table
                        // below it. The table hydrates the full run from `runId`;
                        // legacy outputs without a `runId` fall back to the
                        // persisted sample rows. No result exists until output is
                        // available — before then, fall through to the generic
                        // tool shell (spinner / stale handling).
                        if (
                          toolType === "tool-runQueryTool" &&
                          part.state === "output-available" &&
                          part.output
                        ) {
                          const runOutput = part.output as RunQueryToolOutput;
                          const runInput = part.input as
                            | {
                                query?: string;
                                title?: string;
                                display?: boolean;
                              }
                            | undefined;
                          const sql = runOutput.query ?? runInput?.query ?? "";
                          const runTitle =
                            typeof runInput?.title === "string"
                              ? runInput.title
                              : undefined;
                          // Collapse the result table inside the fold unless the
                          // agent explicitly asked to show it expanded. Strict
                          // `=== true` keeps legacy/partial inputs collapsed.
                          const displayTable = runInput?.display === true;
                          const runHeader = (
                            <ToolHeader
                              title={resolvedTitle}
                              icon={label?.icon}
                              type={toolType}
                              state={part.state}
                              subtitle={
                                inputReady && label?.Subtitle ? (
                                  <label.Subtitle
                                    input={part.input}
                                    output={part.output}
                                  />
                                ) : undefined
                              }
                            />
                          );
                          rendered.push(
                            displayTable ? (
                              <Fragment key={`${id}-${i}`}>
                                <Tool>
                                  {runHeader}
                                  <ToolContent>
                                    <div className="p-3 pt-0">
                                      <CodeBlock code={sql} language="sql" />
                                    </div>
                                  </ToolContent>
                                </Tool>
                                <RunResultTable
                                  output={runOutput}
                                  title={runTitle}
                                />
                              </Fragment>
                            ) : (
                              <Tool key={`${id}-${i}`}>
                                {runHeader}
                                <ToolContent>
                                  <div className="space-y-2 p-3 pt-0">
                                    <CodeBlock code={sql} language="sql" />
                                    <RunResultTable
                                      output={runOutput}
                                      title={runTitle}
                                    />
                                  </div>
                                </ToolContent>
                              </Tool>
                            ),
                          );
                          return;
                        }

                        // createChart: render the call as a compact collapsible
                        // (the chart config) with the live chart rendered
                        // expanded below it. The chart hydrates the full run
                        // from `runId` through the run-only chart rift pipeline.
                        // Creating a chart is inherently answer-bearing, so it
                        // always shows expanded. Before output is available,
                        // fall through to the generic tool shell.
                        if (
                          toolType === "tool-createChartTool" &&
                          part.state === "output-available" &&
                          part.output
                        ) {
                          const chartOutput =
                            part.output as CreateChartToolOutput;
                          const chartInput = part.input as
                            | { title?: string }
                            | undefined;
                          const chartTitle =
                            typeof chartInput?.title === "string"
                              ? chartInput.title
                              : undefined;
                          const chartHeader = (
                            <ToolHeader
                              title={resolvedTitle}
                              icon={label?.icon}
                              type={toolType}
                              state={part.state}
                              subtitle={
                                inputReady && label?.Subtitle ? (
                                  <label.Subtitle
                                    input={part.input}
                                    output={part.output}
                                  />
                                ) : undefined
                              }
                            />
                          );
                          rendered.push(
                            <Fragment key={`${id}-${i}`}>
                              <Tool>
                                {chartHeader}
                                <ToolContent>
                                  <ToolInput input={part.input} />
                                </ToolContent>
                              </Tool>
                              <RunResultChart
                                output={chartOutput}
                                title={chartTitle}
                              />
                            </Fragment>,
                          );
                          return;
                        }

                        const approval = approvals.get(part.toolCallId);
                        const awaitingApproval =
                          !!approval &&
                          part.state !== "output-available" &&
                          part.state !== "output-error";
                        const isStale =
                          !awaitingApproval &&
                          !isBusy &&
                          (part.state === "input-available" ||
                            part.state === "input-streaming");
                        const askUserPayload =
                          awaitingApproval &&
                          toolType === "tool-askUserTool" &&
                          isAskUserSuspendPayload(approval?.suspendPayload)
                            ? (approval?.suspendPayload as AskUserSuspendPayload)
                            : null;
                        rendered.push(
                          <Tool key={`${id}-${i}`}>
                            <ToolHeader
                              title={resolvedTitle}
                              icon={label?.icon}
                              type={toolType}
                              state={
                                awaitingApproval
                                  ? "approval-requested"
                                  : isStale
                                    ? "output-denied"
                                    : part.state
                              }
                              subtitle={
                                inputReady && label?.Subtitle ? (
                                  <label.Subtitle
                                    input={part.input}
                                    output={part.output}
                                  />
                                ) : undefined
                              }
                            />
                            <ToolContent>
                              <ToolInput input={part.input} />
                              <ToolOutput
                                output={part.output}
                                errorText={
                                  isStale
                                    ? "This tool call expired. Please try again."
                                    : part.errorText
                                }
                              />
                            </ToolContent>
                            {awaitingApproval && approval && askUserPayload && (
                              <AskUserForm
                                payload={askUserPayload}
                                disabled={isBusy}
                                onSubmit={(answers: AskUserAnswer[]) =>
                                  onToolResume(approval, {
                                    answers,
                                  } satisfies AskUserResumeData)
                                }
                                onCancel={() =>
                                  onToolResume(approval, {
                                    cancelled: true,
                                  } satisfies AskUserResumeData)
                                }
                              />
                            )}
                            {awaitingApproval &&
                              approval &&
                              !askUserPayload && (
                                <div className="flex items-center gap-2 p-3 pt-0">
                                  <Button
                                    size="sm"
                                    disabled={isBusy}
                                    onClick={() =>
                                      onToolApproval(approval, true)
                                    }
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={isBusy}
                                    onClick={() =>
                                      onToolApproval(approval, false)
                                    }
                                  >
                                    Deny
                                  </Button>
                                </div>
                              )}
                          </Tool>,
                        );
                        return;
                      }
                    }
                  });
                  flushInlineRun();
                  return rendered;
                })()}
              </MessageContent>
            </Message>
          );
        })}
        {isBusy && !hasPendingApprovalIn(messages) && <MessageLoading />}
        <ConversationScrollButton />
      </ConversationContent>
    </Conversation>
  );
};

/**
 * The shape of each placeholder row: a right-aligned user bubble (with a fixed
 * width so the two bubbles differ) or a left-aligned multi-line assistant
 * block. Rendered top-to-bottom like a transcript read from the start.
 */
const SKELETON_ROWS = [
  { role: "user", width: "w-32" },
  { role: "assistant" },
  { role: "user", width: "w-52" },
  { role: "assistant" },
] as const;

/**
 * Placeholder transcript shown while a thread's messages hydrate from the
 * server with nothing cached to paint. Mirrors the alternating user/assistant
 * layout and bottom-anchored padding of {@link ChatMessages} so the real
 * transcript swaps in without a layout jump.
 */
export const ChatMessagesSkeleton = () => (
  <div
    aria-hidden
    className="no-scrollbar flex h-full flex-col justify-start gap-2 overflow-hidden p-2 px-3.5"
  >
    {SKELETON_ROWS.map((row, index) =>
      row.role === "user" ? (
        <div
          key={index}
          className="ml-auto flex w-fit flex-col rounded-lg bg-secondary px-4 py-3"
        >
          <Skeleton className={cn("h-4 bg-muted-foreground/20", row.width)} />
        </div>
      ) : (
        <div key={index} className="flex w-full flex-col gap-2 py-2">
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ),
    )}
  </div>
);
